import { useEffect } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useForm, useStore } from "@tanstack/react-form";
import { Modal } from "../../../components/Modal";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormActions } from "../../../components/FormActions";
import { FormGroup } from "../../../components/FormGroup";
import { PackageCard } from "../../../components/PackageCard";
import { PriceSummarySection } from "./PriceSummarySection";
import { PaymentTermsSection } from "./PaymentTermsSection";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { useSwitchPackageMutation } from "../../../query/billingItemsQuery";
import { useGetStoreAvailableBillingItemsQuery } from "../../../query/availableBillingItemsQuery";
import { toDateString } from "../../../utils/dateUtils";
import { findActivePackageItem } from "../../../utils/billingItemSegments";
import { useIsAdmin } from "../../../hooks/useIsAdmin";

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x4}px;
`;

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${spacing.x2}px;
`;

const ErrorText = styled.div`
  color: ${colors.danger};
  margin-bottom: ${spacing.x1}px;
`;

interface SwitchPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  billingItems: RestBillingItem[] | undefined;
  defaultBillingPeriod?: BillingPeriod;
  defaultStartDate?: string;
}

export const SwitchPackageModal = ({
  isOpen,
  onClose,
  billingItems,
  defaultBillingPeriod = BillingPeriod.MONTHLY,
  defaultStartDate,
}: SwitchPackageModalProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <SwitchPackageModalContent
      onClose={onClose}
      billingItems={billingItems}
      defaultBillingPeriod={defaultBillingPeriod}
      defaultStartDate={defaultStartDate}
    />
  );
};

const SwitchPackageModalContent = ({
  onClose,
  billingItems,
  defaultBillingPeriod = BillingPeriod.MONTHLY,
  defaultStartDate,
}: Omit<SwitchPackageModalProps, "isOpen">) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const isAdmin = useIsAdmin();
  const today = toDateString(new Date());
  const currentPackageId =
    findActivePackageItem(billingItems, today)?.availableBillingItemId ?? null;
  const { data: availableBillingItems } = useGetStoreAvailableBillingItemsQuery(
    storeId ?? null
  );
  const { mutate: switchPackage, isPending, error } =
    useSwitchPackageMutation(storeId ?? null);

  const handleClose = () => {
    onClose();
  };

  const form = useForm({
    defaultValues: {
      selectedPackageId: currentPackageId,
      billingPeriod: defaultBillingPeriod,
      effectiveFrom: defaultStartDate ?? today,
    },
    onSubmit: ({ value }) => {
      if (!value.selectedPackageId) {
        return;
      }
      switchPackage(
        {
          toAvailableBillingItemId: value.selectedPackageId,
          effectiveFrom: value.effectiveFrom,
          billingPeriod: value.billingPeriod,
        },
        { onSuccess: handleClose }
      );
    },
  });

  useEffect(() => {
    if (!currentPackageId || form.state.values.selectedPackageId != null) {
      return;
    }
    form.reset({
      selectedPackageId: currentPackageId,
      billingPeriod: defaultBillingPeriod,
      effectiveFrom: defaultStartDate ?? today,
    });
  }, [currentPackageId, defaultBillingPeriod, defaultStartDate, today, form]);

  const selectedPackageId = useStore(
    form.store,
    (state) => state.values.selectedPackageId
  );
  const billingPeriod = useStore(
    form.store,
    (state) => state.values.billingPeriod
  );
  const effectiveFrom = useStore(
    form.store,
    (state) => state.values.effectiveFrom
  );
  const hasChanged = useStore(form.store, (state) => state.isDirty);

  const packages = (availableBillingItems ?? []).filter(
    (item) => item.type === BillingItemType.PACKAGE
  );

  const selectedPackage =
    packages.find((item) => item.id === selectedPackageId) ?? null;

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title={
        currentPackageId
          ? t("packageSelection.changePackage")
          : t("packageSelection.selectPackage")
      }
      maxWidth={900}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <SectionsContainer>
          <PaymentTermsSection
            paymentTerms={billingPeriod}
            onPaymentTermsChange={(period) =>
              form.setFieldValue("billingPeriod", period)
            }
            startDate={effectiveFrom}
            onStartDateChange={(date) =>
              form.setFieldValue("effectiveFrom", date)
            }
            minStartDate={isAdmin ? undefined : today}
          />

          <FormGroup>
            <ItemGrid>
              {packages.map((item) => (
                <PackageCard
                  key={item.id}
                  id="availableBillingItem"
                  name={item.name}
                  description={item.description}
                  price={item.pricing}
                  selected={item.id === selectedPackageId}
                  onSelect={() =>
                    form.setFieldValue("selectedPackageId", item.id)
                  }
                  selectionType="radio"
                  comingSoon={item.comingSoon ?? false}
                />
              ))}
            </ItemGrid>
          </FormGroup>

          {selectedPackage && (
            <PriceSummarySection
              billingItems={[selectedPackage]}
              paymentTerms={billingPeriod}
            />
          )}

          <Card>
            {error && (
              <ErrorText>
                {t("packageSelectionModal.saveFailed", {
                  message: error.message,
                })}
              </ErrorText>
            )}
            <FormActions>
              <Button
                type="button"
                $color="secondary"
                onClick={handleClose}
                disabled={isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  isPending ||
                  !selectedPackageId ||
                  !hasChanged
                }
              >
                {isPending
                  ? t("packageSelectionModal.saving")
                  : t("common.save")}
              </Button>
            </FormActions>
          </Card>
        </SectionsContainer>
      </Form>
    </Modal>
  );
};
